import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Search, FileText, ArrowUpRight, IndianRupee, ShieldCheck, Smartphone, CreditCard, Building2, QrCode } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";
import { payInvoiceWithRazorpay } from "@/lib/razorpay";

const METHODS = [
  { Icon: Smartphone, label: "UPI" },
  { Icon: QrCode,     label: "QR Code" },
  { Icon: CreditCard, label: "Cards" },
  { Icon: Building2,  label: "Net Banking" },
];

const STATUS_TONE = {
  unpaid:    "border-amber-300 bg-amber-50 text-amber-700",
  paid:      "border-emerald-300 bg-emerald-50 text-emerald-700",
  partial:   "border-blue-300 bg-blue-50 text-blue-700",
  refunded:  "border-zinc-300 bg-zinc-100 text-zinc-700",
  void:      "border-zinc-300 bg-zinc-100 text-zinc-500",
};

const fmtMoney = (amt, cur = "INR") => new Intl.NumberFormat("en-IN", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(amt || 0);

export default function PaymentPortal() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoice, setInvoice] = useState(null);
  const [searching, setSearching] = useState(false);
  const [paying, setPaying] = useState(false);

  const doSearch = useCallback(async (raw) => {
    const inv = (raw || "").trim().toUpperCase();
    if (!inv) return;
    setSearching(true); setInvoice(null);
    try {
      const { data } = await api.get(`/invoices/lookup/${inv}`);
      setInvoice(data);
    } catch (err) {
      toast.error(formatApiError(err) || "Invoice not found");
    } finally { setSearching(false); }
  }, []);

  useEffect(() => {
    const q = params.get("invoice");
    if (q) {
      setInvoiceNumber(q.toUpperCase());
      doSearch(q);
    }
    if (params.get("cancelled")) toast.info("Payment cancelled. You can try again.");
  }, [params, doSearch]);

  const search = (e) => { e?.preventDefault(); doSearch(invoiceNumber); };

  const pay = async () => {
    if (!invoice) return;
    setPaying(true);
    try {
      await payInvoiceWithRazorpay(invoice, (verifyResp) => {
        const inv = verifyResp?.invoice || invoice;
        navigate(`/pay/success?invoice=${inv.invoice_number}&pid=${verifyResp?.payment_id || ""}`);
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setPaying(false); }
  };

  return (
    <div data-testid="pay-page" className="section-pad">
      <div className="container-pp">
        {/* Hero */}
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="editorial-divider mb-5" />
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red flex items-center gap-2"><IndianRupee size={12} /> Payments &amp; Billing</div>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              className="editorial-h1 text-5xl md:text-7xl mt-4 leading-[0.95]">
              Pay your <em className="font-display italic">Pop Panda</em> invoice.
            </motion.h1>
          </div>
          <div className="lg:col-span-4 text-brand-charcoal/75 leading-relaxed">
            <p>Event booking, bulk order, franchise instalment, custom popsicle invoice or a quotation we sent you — settle it here in under a minute.</p>
            <p className="mt-3 flex items-center gap-2 text-brand-charcoal text-sm"><ShieldCheck size={14} /> Encrypted by Stripe. We don't store card details.</p>
          </div>
        </div>

        {/* Methods strip */}
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pay-methods">
          {METHODS.map((m) => (
            <div key={m.label} className="card-elevated p-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-brand-yellow/25 grid place-items-center"><m.Icon size={18} /></div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55">Pay via</div>
                <div className="font-display text-lg">{m.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Search */}
        <form onSubmit={search} className="mt-12 card-elevated p-8 lg:p-10" data-testid="pay-form">
          <div className="grid lg:grid-cols-12 gap-5 items-end">
            <div className="lg:col-span-8">
              <label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Invoice number</label>
              <div className="flex items-center gap-2 mt-2 border-b-2 border-brand-charcoal/15 focus-within:border-brand-charcoal pb-2">
                <FileText size={20} className="text-brand-charcoal/55" />
                <input data-testid="pay-inv-input" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value.toUpperCase())} placeholder="e.g. PP-26-00001" className="bg-transparent border-0 outline-none flex-1 font-display text-2xl tracking-wider" />
              </div>
              <p className="text-xs text-brand-charcoal/55 mt-2">You'll find this on the WhatsApp message, email or printed receipt we sent you.</p>
            </div>
            <div className="lg:col-span-4">
              <button data-testid="pay-search-btn" disabled={searching} className="btn-pill btn-dark w-full justify-center">
                {searching ? "Looking up…" : (<><Search size={16} /> Find invoice</>)}
              </button>
            </div>
          </div>
        </form>

        {/* Invoice card */}
        {invoice && (
          <div className="mt-12 grid lg:grid-cols-12 gap-8 items-start" data-testid={`pay-invoice-${invoice.invoice_number}`}>
            <div className="lg:col-span-7 card-elevated p-8 lg:p-10">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red">Invoice</div>
                <span className={`text-[10px] uppercase tracking-[0.2em] border rounded-full px-3 py-1 ${STATUS_TONE[invoice.payment_status] || ""}`}>{invoice.payment_status}</span>
              </div>
              <div className="font-display text-4xl mt-2">{invoice.invoice_number}</div>
              <div className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
                <div><div className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55">Billed to</div><div className="mt-1 font-medium">{invoice.customer_name}</div></div>
                <div><div className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55">Phone</div><div className="mt-1">{invoice.customer_phone || "—"}</div></div>
                <div><div className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55">Email</div><div className="mt-1">{invoice.customer_email || "—"}</div></div>
                <div><div className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55">Type</div><div className="mt-1 uppercase tracking-[0.18em] text-[11px]">{invoice.invoice_kind}</div></div>
                {invoice.due_date && <div><div className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55">Due date</div><div className="mt-1">{invoice.due_date}</div></div>}
              </div>
              {invoice.items?.length > 0 && (
                <div className="mt-8">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55 mb-3">Line items</div>
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-[0.2em] text-brand-charcoal/55 text-left border-b border-brand-line">
                      <tr><th className="py-2">Item</th><th className="py-2 text-right">Qty</th><th className="py-2 text-right">Unit</th><th className="py-2 text-right">Amount</th></tr>
                    </thead>
                    <tbody>
                      {invoice.items.map((it, i) => (
                        <tr key={i} className="border-b border-brand-line last:border-0">
                          <td className="py-2">{it.description}</td>
                          <td className="py-2 text-right">{it.quantity}</td>
                          <td className="py-2 text-right">{fmtMoney(it.unit_price, invoice.currency)}</td>
                          <td className="py-2 text-right">{fmtMoney(it.amount, invoice.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {invoice.notes && <div className="mt-6 text-sm text-brand-charcoal/70 italic">"{invoice.notes}"</div>}
            </div>

            <div className="lg:col-span-5 rounded-3xl p-8 lg:p-10 bg-brand-charcoal text-brand-cream relative overflow-hidden">
              <div className="absolute -right-20 -top-20 w-60 h-60 rounded-full bg-brand-yellow/15 blur-2xl pointer-events-none" />
              <div className="relative">
              <div className="text-[11px] uppercase tracking-[0.22em] text-brand-yellow">Amount due</div>
              <div className="font-display text-6xl mt-2 leading-none">{fmtMoney(invoice.total_amount, invoice.currency)}</div>
              <div className="mt-4 space-y-1 text-sm text-brand-cream/85">
                <div className="flex justify-between"><span>Subtotal</span><span>{fmtMoney(invoice.subtotal, invoice.currency)}</span></div>
                {invoice.tax > 0 && <div className="flex justify-between"><span>Taxes</span><span>{fmtMoney(invoice.tax, invoice.currency)}</span></div>}
                <div className="flex justify-between font-semibold text-brand-cream pt-2 border-t border-white/15"><span>Total</span><span>{fmtMoney(invoice.total_amount, invoice.currency)}</span></div>
              </div>

              {invoice.payment_status === "paid" ? (
                <div className="mt-8 p-4 rounded-2xl bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 text-sm">
                  This invoice is already paid. Thank you!
                  {invoice.paid_at && <div className="text-xs text-brand-cream/55 mt-1">Paid on {invoice.paid_at.slice(0,10)}</div>}
                </div>
              ) : (
                <button data-testid="pay-pay-btn" onClick={pay} disabled={paying} className="btn-pill btn-primary w-full justify-center mt-8 text-base">
                  {paying ? "Redirecting…" : (<>Pay {fmtMoney(invoice.total_amount, invoice.currency)} now <ArrowUpRight size={18} className="arrow" /></>)}
                </button>
              )}
              <div className="mt-5 text-xs text-brand-cream/60 flex items-center gap-2"><ShieldCheck size={12} /> 256-bit encrypted via Stripe. PCI-DSS Level 1.</div>
              </div>
            </div>
          </div>
        )}

        {!invoice && (
          <div className="mt-16 grid sm:grid-cols-3 gap-5">
            {[
              { t: "1. Search", d: "Enter the invoice number we sent you on WhatsApp, SMS or email." },
              { t: "2. Review", d: "See line items, taxes and due amount in one clean view." },
              { t: "3. Pay", d: "Pay securely via UPI, QR, cards or net banking. Get a receipt instantly." },
            ].map((s) => (
              <div key={s.t} className="card-elevated p-6">
                <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red">{s.t}</div>
                <p className="mt-3 text-brand-charcoal/75 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-brand-charcoal/55 mt-12">
          Don't have an invoice number? <Link to="/contact" className="underline">Reach out</Link> and we'll send you one.
        </p>
      </div>
    </div>
  );
}
